import React, {useMemo} from "react"
import styles from "./DailyAttendanceCalendarView.module.css"
import cx from "classnames"
import {roundTwoDigits} from "helpers"
import {AttendancePriorTotals} from "sections/shared/attendanceCalendarUtils"

type SummaryRow = {
    label: string
    total?: number | null
    priorTotal?: number | null
    grandTotal?: number | null
}

type SummaryViewProps = {
    dailyAttendanceSummaryData: any
    title?: string
    includeLoaDays?: boolean
    includeScheduleHoursToLda?: boolean
    className?: string
    showPriorTotal?: boolean
    priorTotals?: AttendancePriorTotals | null
    showGrandTotal?: boolean
    totalColumnLabel?: string
    grandTotalColumnLabel?: string
}

const sumPriorAndMonth = (prior?: number | null, total?: number | null) => Number(prior ?? 0) + Number(total ?? 0)

const SummaryView = ({
    dailyAttendanceSummaryData,
    title = "Summary",
    includeLoaDays = true,
    includeScheduleHoursToLda = true,
    className,
    showPriorTotal = false,
    priorTotals,
    showGrandTotal = false,
    totalColumnLabel = "Total",
    grandTotalColumnLabel = "Grand Total"
}: SummaryViewProps) => {
    const tableData = useMemo((): SummaryRow[] => {
        const {
            totalAttendedHours,
            totalMissedHours,
            totalMakeUpHours,
            totalDistEdHours,
            totalScheduleHours,
            totalScheduleHoursToLda,
            totalLoaDays,
            totalSuspendedDays
        } = dailyAttendanceSummaryData ?? {}

        const scheduleHours = totalScheduleHours ?? 0
        const attendedHours = totalAttendedHours ?? 0
        const missedHours = totalMissedHours ?? 0
        const makeUpHours = totalMakeUpHours ?? 0
        const distEdHours = totalDistEdHours ?? 0

        const rows: SummaryRow[] = [
            {
                label: "Scheduled Hours",
                total: scheduleHours,
                priorTotal: priorTotals?.totalScheduleHours ?? 0,
                grandTotal: sumPriorAndMonth(priorTotals?.totalScheduleHours, scheduleHours)
            },
            {
                label: "Attended Hours",
                total: attendedHours,
                priorTotal: priorTotals?.totalAttendedHours ?? 0,
                grandTotal: sumPriorAndMonth(priorTotals?.totalAttendedHours, attendedHours)
            },
            {
                label: "Absent Hours",
                total: missedHours,
                priorTotal: priorTotals?.totalMissedHours ?? 0,
                grandTotal: sumPriorAndMonth(priorTotals?.totalMissedHours, missedHours)
            },
            {
                label: "Makeup Hours",
                total: makeUpHours,
                priorTotal: priorTotals?.totalMakeUpHours ?? 0,
                grandTotal: sumPriorAndMonth(priorTotals?.totalMakeUpHours, makeUpHours)
            },
            {
                label: "Distance Ed Hours",
                total: distEdHours,
                priorTotal: priorTotals?.totalDistEdHours ?? 0,
                grandTotal: sumPriorAndMonth(priorTotals?.totalDistEdHours, distEdHours)
            }
        ]

        if (includeScheduleHoursToLda) {
            rows.push({
                label: "Scheduled Hours To LDA",
                grandTotal: totalScheduleHoursToLda ?? 0,
                total: null,
                priorTotal: null
            })
        }

        if (includeLoaDays) {
            rows.push({label: "Loa Days", grandTotal: totalLoaDays ?? 0, total: null, priorTotal: null})
            rows.push({
                label: "Suspended Days",
                grandTotal: totalSuspendedDays ?? 0,
                total: null,
                priorTotal: null
            })
        }

        return rows
    }, [dailyAttendanceSummaryData, includeLoaDays, includeScheduleHoursToLda, priorTotals])

    return (
        <div className={cx(styles.summaryContainer, className)}>
            <div
                className={cx(
                    styles.summaryWrap,
                    showPriorTotal && !showGrandTotal && styles.summaryWrapWithPrior,
                    showPriorTotal && showGrandTotal && styles.summaryWrapWithPriorAndGrand
                )}>
                <div className={styles.summaryTitle}>{title}</div>
                <table className={styles.table}>
                    <thead>
                        <tr className={styles.headerRow}>
                            <th className={styles.th}>Type</th>
                            {showPriorTotal ? <th className={cx(styles.th, styles.thRight)}>Prior Total</th> : null}
                            <th className={cx(styles.th, styles.thRight)}>{totalColumnLabel}</th>
                            {showGrandTotal ? (
                                <th className={cx(styles.th, styles.thRight)}>{grandTotalColumnLabel}</th>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row) => (
                            <tr key={row.label}>
                                <td className={cx(styles.td, styles.rowLabel)}>{row.label}</td>
                                {showPriorTotal ? (
                                    <td className={styles.td}>
                                        {row.priorTotal === null ? "" : roundTwoDigits(row.priorTotal)}
                                    </td>
                                ) : null}
                                <td className={styles.td}>{row.total === null ? "" : roundTwoDigits(row.total)}</td>
                                {showGrandTotal ? (
                                    <td className={styles.td}>
                                        {row.grandTotal === null ? "" : roundTwoDigits(row.grandTotal)}
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default SummaryView
